import React from "react";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useRoomUsageHistory } from "@/hooks/useRooms";
import type { DateRange } from "@/hooks/useDateRange";
import { toRoomUsageApiRange } from "@/lib/roomUsage";
import type { RoomUsageStay } from "@/types/roomUsage";
import { formatDateTime } from "@/components/pages/reservations/utils/formatDateTime";
import { formatStatusLabel } from "@/components/pages/reservations/utils/formatStatusLabel";
import { RoomHistoryPerformanceCard } from "./RoomHistoryPerformanceCard";

interface RoomUsageHistorySectionProps {
  hotelId: string;
  roomId: string;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

export function RoomUsageHistorySection({
  hotelId,
  roomId,
  dateRange,
  onDateRangeChange,
}: RoomUsageHistorySectionProps) {
  const { t } = useTranslation();
  const apiRange = React.useMemo(
    () => toRoomUsageApiRange(dateRange),
    [dateRange],
  );

  const usageHistoryQuery = useRoomUsageHistory(hotelId, roomId, apiRange);
  const usageHistory = usageHistoryQuery.data;
  const columns = React.useMemo<Column<RoomUsageStay>[]>(
    () => [
      {
        key: "guest.fullName",
        header: t("reservation.roomUsage.guest"),
      },
      {
        key: "arrivalDate",
        header: t("reservation.roomUsage.arrival"),
        render: (value: string) => formatDateTime(value, "d MMM yyyy"),
      },
      {
        key: "departureDate",
        header: t("reservation.roomUsage.departure"),
        render: (value: string) => formatDateTime(value, "d MMM yyyy"),
      },
      {
        key: "nights",
        header: t("reservation.roomUsage.nights"),
      },
      {
        key: "nightsInRange",
        header: t("reservation.roomUsage.nightsInRange"),
      },
      {
        key: "status",
        header: t("reservation.roomUsage.status"),
        render: (value: string) => formatStatusLabel(value),
      },
    ],
    [t],
  );

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            {t("reservation.roomUsage.roomSummaryTitle")}
          </h2>
          {usageHistory ? (
            <p className="text-sm text-muted-foreground">
              {usageHistory.roomNumber}
              {usageHistory.category?.name
                ? ` • ${usageHistory.category.name}`
                : ""}
            </p>
          ) : null}
        </div>

        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
        />
      </div>

      <RoomHistoryPerformanceCard
        history={usageHistory}
        isLoading={usageHistoryQuery.isLoading}
      />

      <div className="mt-6">
        <h3 className="mb-3 text-lg font-semibold">
          {t("reservation.roomUsage.historyTitle")}
        </h3>
        <DataTable
          data={usageHistory?.stays ?? []}
          columns={columns}
          getRowKey={(row) => row.bookingId}
          loading={usageHistoryQuery.isLoading}
          withPagination={false}
          withSorting={false}
          emptyMessage={t("reservation.roomUsage.historyEmpty")}
        />
      </div>
    </section>
  );
}
